import { istMidnightUtcMs, istToday } from "./dateRange";

export const LIVE_STATUSES = ["RUNNING", "PAUSED", "AUTO_PAUSED"];

export function rangeIncludesToday(from, to, now = new Date()) {
  if (!from || !to) return false;
  const today = istToday(now);
  return from <= today && today <= to;
}

export function isLiveStatus(status) {
  return LIVE_STATUSES.includes(status);
}

export function liveSession(sessions = []) {
  return sessions.find((session) => isLiveStatus(session.status)) || null;
}

export function buildPeopleRows({ signedInUsers = [], sessions = [] } = {}) {
  const grouped = new Map();
  for (const session of sessions) {
    const userId = String(session.userId || "").trim().toLowerCase();
    if (!userId) continue;
    if (!grouped.has(userId)) grouped.set(userId, []);
    grouped.get(userId).push(session);
  }

  const roster = signedInUsers.length
    ? signedInUsers
    : [...grouped.keys()].map((userId) => {
      const first = grouped.get(userId)[0];
      return {
        userId,
        userName: first?.userName || userId,
        hasActivity: true,
      };
    });

  return roster
    .map((person) => {
      const userId = String(person.userId || "").trim().toLowerCase();
      const userSessions = grouped.get(userId) || [];
      const live = liveSession(userSessions);
      return {
        userId,
        userName: person.userName || userId,
        hasActivity: userSessions.length > 0,
        sessionCount: userSessions.length,
        activeSeconds: userSessions.reduce((sum, item) => sum + (Number(item.activeSeconds) || 0), 0),
        pausedSeconds: userSessions.reduce((sum, item) => sum + (Number(item.pausedSeconds) || 0), 0),
        status: live?.status || null,
      };
    })
    .sort((left, right) => {
      if (right.activeSeconds !== left.activeSeconds) {
        return right.activeSeconds - left.activeSeconds;
      }
      return String(left.userName || "").localeCompare(String(right.userName || ""));
    });
}

export function trackerStateCounts(peopleRows = []) {
  return peopleRows.reduce(
    (counts, row) => {
      if (row.status === "RUNNING") counts.running += 1;
      else if (row.status === "PAUSED") counts.paused += 1;
      else if (row.status === "AUTO_PAUSED") counts.autoPaused += 1;
      else counts.notTracking += 1;
      return counts;
    },
    {
      running: 0,
      paused: 0,
      autoPaused: 0,
      notTracking: 0,
    },
  );
}

function overlapSeconds(startMs, endMs, windowStart, windowEnd) {
  const from = Math.max(startMs, windowStart);
  const to = Math.min(endMs, windowEnd);
  return Math.max(0, Math.floor((to - from) / 1000));
}

export function activeSecondsByUserDay({
  people = [],
  sessions = [],
  dates = [],
  now = new Date(),
} = {}) {
  const nowMs = now.getTime();
  return people.map((person) => {
    const cells = Object.fromEntries((dates || []).map((date) => [date, 0]));
    const userSessions = sessions.filter(
      (session) => String(session.userId || "").trim().toLowerCase() === person.userId,
    );
    for (const session of userSessions) {
      for (const interval of session.intervals || []) {
        if (interval.intervalType !== "ACTIVE") continue;
        const start = new Date(interval.startTime).getTime();
        const end = interval.endTime ? new Date(interval.endTime).getTime() : nowMs;
        if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
        for (const date of dates) {
          const windowStart = istMidnightUtcMs(date);
          const windowEnd = windowStart + 24 * 60 * 60 * 1000;
          cells[date] += overlapSeconds(start, end, windowStart, windowEnd);
        }
      }
    }
    return {
      userId: person.userId,
      userName: person.userName,
      cells,
    };
  });
}

export function filterSessionHistory(sessions = [], { status = "all", sort = "newest" } = {}) {
  let rows = Array.isArray(sessions) ? [...sessions] : [];
  if (status === "live") {
    rows = rows.filter((session) => isLiveStatus(session.status));
  } else if (status === "completed") {
    rows = rows.filter((session) => !isLiveStatus(session.status));
  }
  rows.sort((left, right) => {
    const a = new Date(left.startTime).getTime();
    const b = new Date(right.startTime).getTime();
    const leftTime = Number.isFinite(a) ? a : 0;
    const rightTime = Number.isFinite(b) ? b : 0;
    return sort === "oldest" ? leftTime - rightTime : rightTime - leftTime;
  });
  return rows;
}
